import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { inrFull, fdt } from "@/lib/format";
import { ClipboardList, Scissors, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, v, tid, tone = "text-slate-900" }) {
  return <div className="rounded-xl border border-slate-100 p-3"><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p><p className={`text-xl font-extrabold tabular ${tone}`} data-testid={tid}>{v ?? 0}</p></div>;
}

function JobRow({ it, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`rq-item-${it.ticket_id}`}>
      <div className="min-w-0 text-sm">
        <p className="font-semibold text-slate-900">{it.pet_name} <span className="font-normal text-slate-500">· {it.customer_name}{it.customer_mobile ? ` · ${it.customer_mobile}` : ""}</span></p>
        <p className="text-xs text-slate-500">{it.ticket_no ? `${it.ticket_no} · ` : ""}{it.service_name} · {it.staff_name}</p>
        {it.reason && <p className="text-xs text-rose-700">Reason: {it.reason}{it.unable_by_name ? ` (by ${it.unable_by_name}, ${fdt(it.unable_at)})` : ""}</p>}
      </div>
      {children}
    </div>
  );
}

export function ReceptionWorkQueue() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isError } = useQuery({ queryKey: ["reception-work-queue"], refetchInterval: 60000, retry: false, queryFn: async () => (await api.get("/reception/work-queue")).data });
  const ack = useMutation({ mutationFn: (id) => api.post(`/grooming/tickets/${id}/unable-ack`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["reception-work-queue"] }); toast.success("Marked handled"); }, onError: (e) => toast.error(apiErr(e)) });
  const bill = useMutation({ mutationFn: async (id) => (await api.post(`/grooming/tickets/${id}/create-invoice`)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["reception-work-queue"] }); toast.success(`Bill ${d.invoice_number} created`); navigate(`/pos?invoice=${d.invoice_id}`); },
    onError: (e) => toast.error(apiErr(e)) });
  const open = (it) => {
    if (it.booking_id) navigate(`/pos?booking=${it.booking_id}`);
    else if (it.invoice_id) navigate(`/pos?invoice=${it.invoice_id}`);
    else bill.mutate(it.ticket_id);
  };
  if (isError || !data) return null;
  const c = data.counts;
  return (
    <div className="mb-6 rounded-2xl border border-pawkt-border bg-white p-4" data-testid="reception-work-queue">
      <p className="mb-3 flex items-center gap-2 font-display font-bold text-pawkt-text"><ClipboardList className="h-4 w-4 text-sky-500" /> Reception work queue</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat label="Consults not billed" v={c.consult_not_billed} tid="rq-count-consult" />
        <Stat label="Partially billed" v={c.consult_partially_billed} tid="rq-count-partial" />
        <Stat label="Manual fee needed" v={c.consult_manual_fee} tid="rq-count-manual" tone="text-amber-700" />
        <Stat label="Grooming checkout" v={c.grooming_checkout} tid="rq-count-groom" />
        <Stat label="Grooming unable" v={c.grooming_unable} tid="rq-count-unable" tone="text-rose-700" />
      </div>
      {data.grooming_checkout.length > 0 && (
        <div className="mt-4"><p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Scissors className="h-3.5 w-3.5" /> Grooming completed — awaiting checkout (not yet paid)</p>
          <div className="divide-y divide-slate-100">{data.grooming_checkout.map((it) => (
            <JobRow key={it.ticket_id} it={it}>
              {it.next_action === "create_bill"
                ? <span className="text-xs text-amber-700" data-testid={`rq-nobill-${it.ticket_id}`}>No bill yet</span>
                : <span className="text-xs text-slate-500">{it.invoice_number} · due {inrFull(it.balance)}</span>}
              <button onClick={() => open(it)} disabled={bill.isPending} data-testid={`rq-collect-${it.ticket_id}`}
                className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white">{it.next_action === "create_bill" ? "Create bill" : "Collect payment"}</button>
            </JobRow>))}</div></div>
      )}
      {data.grooming_unable.length > 0 && (
        <div className="mt-4"><p className="flex items-center gap-1.5 text-xs font-semibold text-rose-700"><AlertTriangle className="h-3.5 w-3.5" /> Grooming unable to complete — contact customer</p>
          <div className="divide-y divide-slate-100">{data.grooming_unable.map((it) => (
            <JobRow key={it.ticket_id} it={it}>
              <button onClick={() => ack.mutate(it.ticket_id)} data-testid={`rq-ack-${it.ticket_id}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700">Mark handled</button>
            </JobRow>))}</div></div>
      )}
      {!data.grooming_checkout.length && !data.grooming_unable.length && !c.consult_not_billed && !c.consult_partially_billed && (
        <p className="mt-3 text-sm text-pawkt-text-secondary" data-testid="rq-empty">Nothing waiting for reception right now.</p>)}
    </div>
  );
}
